import { useSyncExternalStore } from 'react';

/**
 * The phone's text size setting drives the root font size, so every rem in
 * the UI follows it. Android Chrome does this itself (the `text-scale` meta in
 * index.html). iOS Safari only exposes Dynamic Type through the
 * `-apple-system-body` font, which is 17px at the default setting, so we
 * measure it and set the root to 16px times that ratio.
 *
 * Work areas with their own pinch zoom (the grid) divide this back out with
 * systemTextScale(), so they look the same at every setting.
 */
const IOS_DEFAULT_BODY_PX = 17;
const CHANGE_EVENT = 'nt-system-text-scale';

function applyIosDynamicType(): void {
  if (!CSS.supports('font', '-apple-system-body')) return;
  const probe = document.createElement('span');
  probe.style.cssText = 'font: -apple-system-body; position: absolute; visibility: hidden;';
  document.body.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).fontSize);
  probe.remove();
  if (px > 0) document.documentElement.style.fontSize = `${(16 * px) / IOS_DEFAULT_BODY_PX}px`;
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function installSystemTextScale(): void {
  applyIosDynamicType();
  // iOS changes Dynamic Type from Settings, so the app comes back from the background.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') applyIosDynamicType();
  });
  // Android Chrome resizes the root itself; a resize follows it.
  window.addEventListener('resize', () => window.dispatchEvent(new Event(CHANGE_EVENT)));
}

/** Root font size relative to 16px: 1 at the phone's default text size. */
export function systemTextScale(): number {
  const px = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return px > 0 ? px / 16 : 1;
}

/** systemTextScale() as React state: re-renders when the phone's text size changes. */
export function useSystemTextScale(): number {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener(CHANGE_EVENT, onChange);
      return () => window.removeEventListener(CHANGE_EVENT, onChange);
    },
    systemTextScale,
  );
}
